import { useEffect, useEffectEvent, useState } from 'react';

const COUNTDOWN_INTERVAL_MS = 1000;
const THROTTLE_DELAY_MS = 300;

type Props = {
  isActive: boolean;
  inactivityBeforeCountdownMs: number;
  startCountdownValue: number;
  onCountdownEnded: () => void;
};

export const useInactivityTimer = ({
  isActive,
  inactivityBeforeCountdownMs,
  startCountdownValue,
  onCountdownEnded,
}: Props) => {
  const [countdown, setCountdown] = useState<number | null>(null);
  const onCountdownEndedEvent = useEffectEvent(onCountdownEnded);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCountdown(null);

    if (!isActive) return;

    let countdownIntervalCurrent: number | undefined;

    const startInactivityTimer = () => {
      return window.setTimeout(() => {
        setCountdown(startCountdownValue);
        let currentCountdown = startCountdownValue;

        const countdownInterval = window.setInterval(() => {
          currentCountdown -= 1;
          setCountdown(currentCountdown);

          if (currentCountdown === 0) {
            clearInterval(countdownInterval);
            onCountdownEndedEvent();
          }
        }, COUNTDOWN_INTERVAL_MS);

        countdownIntervalCurrent = countdownInterval;
      }, inactivityBeforeCountdownMs);
    };

    let inactivityTimeout = startInactivityTimer();

    const resetTimers = () => {
      window.clearTimeout(inactivityTimeout);
      window.clearInterval(countdownIntervalCurrent);
      countdownIntervalCurrent = undefined;

      inactivityTimeout = startInactivityTimer();
      setCountdown(null);
    };

    let lastCall = 0;
    const throttledResetTimers = () => {
      const now = Date.now();
      if (now - lastCall >= THROTTLE_DELAY_MS) {
        lastCall = now;
        resetTimers();
      }
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach((event) => document.addEventListener(event, throttledResetTimers, true));

    return () => {
      window.clearTimeout(inactivityTimeout);
      window.clearInterval(countdownIntervalCurrent);
      events.forEach((event) => document.removeEventListener(event, throttledResetTimers, true));
    };
  }, [isActive, inactivityBeforeCountdownMs, startCountdownValue]);

  return {
    countdown,
  };
};
